import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {finalize, map, switchMap} from 'rxjs/operators';
import {UserModel} from '../_models/user.model';
import {AuthModel} from '../_models/auth.model';
import {AuthHTTPService} from './auth-http';
import {environment} from 'src/environments/environment';
import {Router} from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // public fields
  currentUser$: Observable<UserModel>;
  tempUser$: Observable<UserModel>;
  currentUserToken$: Observable<string>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  tempUserSubject: BehaviorSubject<UserModel>;
  currentUserTokenSubject: BehaviorSubject<string>;
  isLoadingSubject: BehaviorSubject<boolean>;
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  constructor(
    private authHttpService: AuthHTTPService,
    // private toastService: ToastService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.tempUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUserTokenSubject = new BehaviorSubject<string>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.tempUser$ = this.tempUserSubject.asObservable();
    this.currentUserToken$ = this.currentUserTokenSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }
  get tempUserValue(): UserModel {
    return this.tempUserSubject.value;
  }

  set tempUserValue(user: UserModel) {
    this.tempUserSubject.next(user);
  }
  get currentUserTokenValue(): string {
    return this.currentUserTokenSubject.value;
  }

  set currentUserTokenValue(token: string) {
    this.currentUserTokenSubject.next(token);
  }

  // public methods
  // login(username: string, password: string): Observable<UserModel> {
  //     this.isLoadingSubject.next(true);
  //     return this.authHttpService.login(username, password).pipe(
  //         map((response: any) => {
  //           return this.setAuthFromLocalStorage(response, response.auth);
  //         }),
  //         switchMap(() => this.getUserByToken()),
  //         finalize(() => this.isLoadingSubject.next(false))
  //     );
  // }

  login(username: string, password: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(username, password).pipe(
      map((response: any) => {
        // Store auth data if TFA is enabled
        const isTFAEnabled = response.user.isTFAEnabled;
        if (!isTFAEnabled) {
          this.setAuthFromLocalStorage(response, response.auth);
        }
        return { user: response.user, isTFAEnabled };
      }),
      switchMap(({ user, isTFAEnabled }) => {
        // Only get user by token if TFA is enabled
        if (!isTFAEnabled) {
          return this.getUserByToken();
        }
        this.tempUserSubject.next(user);
        return of(user);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  // public methods
  update(companyName: string, companyPhone: string, companyEmail: string, companyIcon: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.update(companyName, companyPhone, companyEmail, companyIcon, this.currentUserTokenValue).pipe(
      switchMap(() => this.getUserByToken()),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // public methods
  notifications(): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.notifications(this.currentUserTokenValue).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // public methods
  getNotification(userNotificationId): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.getNotification(userNotificationId, this.currentUserTokenValue).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // public methods
  changePassword(currentPassword: string, newPassword: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.changePassword(currentPassword, newPassword, this.currentUserTokenValue).pipe(
      switchMap(() => this.getUserByToken()),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageToken);
    this.authHttpService.logout(localStorage.getItem('fcm_token'), this.currentUserTokenValue).pipe(
      switchMap(() => this.getUserByToken())
    );
    localStorage.removeItem('fcm_token');
    localStorage.removeItem('systemPackage');
    localStorage.removeItem('clientType');
    localStorage.removeItem('systemRoleId');
    localStorage.removeItem('loglevel');
    localStorage.removeItem('company');
    localStorage.removeItem('redirect_url');

    localStorage.removeItem('systemFacilities');

    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserModel> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.token) {
      return of(undefined);
    }
    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.token).pipe(
      map((user: UserModel) => {
        if (user) {
          this.currentUserSubject.next(user);
          this.currentUserTokenSubject = new BehaviorSubject<string>(auth.token);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  // registration(user: UserModel): Observable<any> {
  //   this.isLoadingSubject.next(true);
  //   return this.authHttpService.createUser(user).pipe(
  //     map(() => {
  //       this.isLoadingSubject.next(false);
  //     }),
  //     switchMap(() => this.login(user.username, user.password)),
  //     catchError((err) => {
  //       console.error('err', err);
  //       return of(undefined);
  //     }),
  //     finalize(() => this.isLoadingSubject.next(false))
  //   );
  // }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  // private methods
  setAuthFromLocalStorage(res, auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.token ) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      localStorage.setItem('company', JSON.stringify(res.user.company));
      localStorage.setItem('systemPackage', JSON.stringify(JSON.parse(res?.user.system_package_id).types));
      localStorage.setItem('redirect_url', JSON.stringify(JSON.parse(res?.user.system_package_id).redirect_url));

      if (JSON.parse(res?.user.system_package_id).client_type === 'PAYER') {
        localStorage.setItem('clientType', JSON.stringify('PAYER'));

      }else{
        localStorage.setItem('clientType', JSON.stringify('PROVIDER'));

      }
      localStorage.setItem('systemFacilities', JSON.stringify(res?.user.subscriptions[0].facilities));
      localStorage.setItem('systemRoleId', JSON.stringify(res?.user.systemRoleId));
      this.saveRoleIdsToLocalStorage(res?.user.subscriptions);
      return true;
    }
    return false;
  }

  saveRoleIdsToLocalStorage(subscriptions) {
    const roleIds = [];
    subscriptions.forEach(subscription => {
      if (subscription.role && subscription.role.system_role_id) {
        roleIds.push(subscription.role.system_role_id);
      }
    });
    localStorage.setItem('systemRoleId', JSON.stringify(roleIds));
  }
  private getAuthFromLocalStorage(): AuthModel {
    try {
      const authData = JSON.parse(
        localStorage.getItem(this.authLocalStorageToken)
      );
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}
